import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";
import DobField from "@/components/DobField";
import useSeo from "@/components/useSeo";

const ROLES = [
  ["coach", "Club"],
  ["parent", "Parent"],
  ["player", "Player 18+"],
];

const COPY = {
  coach: { title: "Create your club", sub: "Free forever. Up to 15 athletes." },
  parent: { title: "Join as a parent", sub: "Track your child and pay fees." },
  player: { title: "Join as a player", sub: "Track your training, schedule and fees." },
};

function ageFrom(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return a;
}

export default function Register() {
  const inviteParams = new URLSearchParams(window.location.search);
  const invitedRole = ["coach", "parent", "player"].includes(inviteParams.get("role")) ? inviteParams.get("role") : "coach";
  const inviteMode = inviteParams.get("invite") === "1";
  const [name, setName] = useState("");
  const [email, setEmail] = useState(inviteParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(invitedRole);
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Create your club free · SphereHyx",
    description: "Create your sports club free on SphereHyx. Manage teams, athletes, attendance, payments and tournaments — up to 15 athletes on the free plan.",
    keywords: "create sports club, free club management, sign up sports club software",
    canonical: "https://spherehyx.com/register",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (role === "player") {
      const a = ageFrom(dob);
      if (a === null) return toast.error("Please enter your date of birth");
      if (a < 18) return toast.error("You must be 18 or older to use the Players Portal");
    }
    setLoading(true);
    try {
      const payload = { name, email, password, role };
      if (role === "player") payload.dob = dob;
      const { data } = await api.post("/auth/register", payload);
      if (data?.token) localStorage.setItem("cs_token", data.token);
      await refresh();
      navigate(role === "parent" ? "/parent" : role === "player" ? "/portal" : "/onboarding");
    } catch (err) {
      toast.error(apiErr(err, "Could not create account"));
    } finally {
      setLoading(false);
    }
  };

  const copy = inviteMode
    ? { title: role === "player" ? "Set up your player portal" : "Set up your parent portal",
        sub: "Create your account — it links to your athlete profile automatically." }
    : COPY[role];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={34} /></div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">{copy.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{copy.sub}</p>

          {!inviteMode && (
          <div className="grid grid-cols-3 gap-2 mt-5 p-1 bg-accent rounded-xl">
            {ROLES.map(([r, l]) => (
              <button key={r} type="button" onClick={() => setRole(r)} data-testid={`role-${r}`}
                className={`h-9 rounded-lg text-sm font-medium transition-colors duration-150 ${role === r ? "brand-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          )}

          <GoogleButton role={role} testid="google-signup-btn" className="w-full mt-5 h-11" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="register-password-input" />
              <p className="text-xs text-muted-foreground mt-1">At least 8 characters, with a letter and a number.</p>
            </div>
            {role === "player" && (
              <div>
                <Label htmlFor="dob">Date of birth</Label>
                <DobField value={dob} onChange={setDob} testid="register-dob" />
                <p className="text-xs text-muted-foreground mt-1">You must be 18 or older for the Players Portal. Use the same email your coach has on file so your profile links automatically.</p>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11 brand-gradient text-white border-0 hover:opacity-90" data-testid="register-submit-btn">
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1526232761682-d26e03ac148e?auto=format&fit=crop&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tl from-background via-background/60 to-transparent" />
        <div className="absolute bottom-12 right-12 text-right">
          <Logo size={38} />
          <p className="mt-4 font-heading font-bold text-3xl max-w-sm tracking-tight">Coach. Manage. Grow.</p>
        </div>
      </div>
    </div>
  );
}
